import { api } from "@/services/api"
import type { AmsFundingProgram, FundingProgramState } from "../booking-season/bookingSeasonApi"

// ── Types ────────────────────────────────────────────────────────────
// Mirror AmsFundingSwitchesController.cs and FundingSwitches.cs (AB#607). Programs arrive by name.

/** FundingSwitchHistoryEntry: one saved switch, as saved. Staff only. */
export interface FundingSwitchHistoryEntry {
  program: AmsFundingProgram
  isEnabled: boolean
  /** The message saved with it; null when none was, so the program's default applied. */
  message: string | null
  /** UPN of whoever saved it. */
  createdBy: string
  /** UTC. */
  createdAt: string
}

/** FundingSwitchesResponse: GET /api/ams/funding-switches, and the body of a successful save. */
export interface FundingSwitchesResponse {
  /** Every program's current switch, in enum order. */
  programs: FundingProgramState[]
  /** The newest saved switches across every program, newest first. */
  history: FundingSwitchHistoryEntry[]
}

/** SaveFundingSwitchRequest: the body of PUT /api/ams/funding-switches. */
export interface SaveFundingSwitchRequest {
  program: AmsFundingProgram
  isEnabled: boolean
  /** Blank or null uses the program's default message. */
  message: string | null
}

/** The body of a 400 from PUT: `saved` is always null then. */
export interface FundingSwitchSaveRefusal {
  saved: null
  errors: string[]
}

// ── Staff endpoints (Entra token) ────────────────────────────────────

// AmsBookingSeason too: a save changes what the season reads as (its programs, and closed when every
// program is off), so the season page and the calendar banner refetch.
const taggedApi = api.enhanceEndpoints({ addTagTypes: ["AmsFundingSwitches", "AmsBookingSeason"] })

export const fundingSwitchesApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Requires appointments:view or appointments:manage-booking-season. */
    getFundingSwitches: builder.query<FundingSwitchesResponse, void>({
      query: () => ({ url: "/api/ams/funding-switches" }),
      providesTags: ["AmsFundingSwitches"],
    }),
    /** Requires appointments:manage-booking-season. 400 carries a FundingSwitchSaveRefusal. */
    saveFundingSwitch: builder.mutation<FundingSwitchesResponse, SaveFundingSwitchRequest>({
      query: (body) => ({ url: "/api/ams/funding-switches", method: "PUT", body }),
      invalidatesTags: ["AmsFundingSwitches", "AmsBookingSeason"],
    }),
  }),
})

export const { useGetFundingSwitchesQuery, useSaveFundingSwitchMutation } = fundingSwitchesApi
